import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { UIRouterModule } from '@uirouter/angular';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ComponentsModule } from '../components/components.module';
import { RvSortableModule } from '../rv-sortable/rv-sortable.module';
import { SharedModule } from '../shared/shared.module';
import { routes } from './editor.routes';

import { EditorService } from './services/editor.service';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { AIChatbotModule } from '../ai-chatbot/ai-chatbot.module';
import { FolderPathComponent } from '../components/folders/folder-path/folder-path.component';
import { NewFolderButtonComponent } from '../components/folders/new-folder-button/new-folder-button.component';
import { NoResultsComponent } from '../components/no-results/no-results.component';
import { OperationsMenuComponent } from '../components/operations-menu/operations-menu.component';
import { StatusBadgeComponent } from '../components/status-badge/status-badge.component';
import { PurchaseModule } from '../purchase/purchase.module';
import { UsernamePipe } from '../shared/pipes/username.pipe';
import { AddPresentationComponent } from './components/add-presentation/add-presentation.component';
import { ArtboardPlaceholderComponent } from './components/artboard-placeholder/artboard-placeholder.component';
import { ArtboardPresentationComponent } from './components/artboard-presentation/artboard-presentation.component';
import { ArtboardComponent } from './components/artboard/artboard.component';
import { EmbeddingPresentationsModalComponent } from './components/embedding-presentations-modal/embedding-presentations-modal.component';
import { FooterComponent } from './components/footer/footer.component';
import { HtmlEditorComponent } from './components/html-editor/html-editor.component';
import { PlaceholderPlaylistComponent } from './components/placeholder-playlist/placeholder-playlist.component';
import { PlaceholderSettingsComponent } from './components/placeholder-settings/placeholder-settings.component';
import { PlaceholdersListComponent } from './components/placeholders-list/placeholders-list.component';
import { PlaylistItemComponent } from './components/playlist-item/playlist-item.component';
import { PresentationItemComponent } from './components/presentation-item/presentation-item.component';
import { PresentationListComponent } from './components/presentation-list/presentation-list.component';
import { PresentationPropertiesComponent } from './components/presentation-properties/presentation-properties.component';
import { ProductDetailsComponent } from './components/product-details/product-details.component';
import { ResolutionSelectorComponent } from './components/resolution-selector/resolution-selector.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { StoreContentComponent } from './components/store-content/store-content.component';
import { TemplateCalendarComponent } from './components/template-calendar/template-calendar.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { WeeklyTemplatesComponent } from './components/weekly-templates/weekly-templates.component';
import { WidgetItemComponent } from './components/widget-item/widget-item.component';
import { WidgetSettingsComponent } from './components/widget-settings/widget-settings.component';
import { WorkspaceComponent } from './components/workspace/workspace.component';
import { PlaceholderDragDirective } from './directives/placeholder-drag.directive';
import { PlaceholderNameDirective } from './directives/placeholder-name.directive';
import { PlaceholderResizeDirective } from './directives/placeholder-resize.directive';

import { DialogHeaderComponent } from '../components/modals/dialog-header/dialog-header.component';
import { RequestChangesDialogComponent } from './components/request-changes-dialog/request-changes-dialog.component';
import { ReviewRequestComponent } from './components/review-request/review-request.component';
import { WorkspaceElementDirective } from './directives/workspace-element.directive';
import { providePresentationFolderOperationService } from './providers/presentation-folder-operation.provider';
import { ViewportComponent } from './viewport/viewport.component';
import { AccessibilityModule } from '../accessibility/accessibility.module';
import { RvDraggableModule } from '../rv-draggable/rv-draggable.module';
import { ActionLinkDirective } from '../accessibility/directives/action-link/action-link.directive';

@NgModule({
  imports: [
    ActionLinkDirective,
    CommonModule,
    FormsModule,
    UIRouterModule.forChild(routes),
    BrowserAnimationsModule,
    ModalModule,
    TooltipModule,
    PopoverModule,
    CodemirrorModule,
    ComponentsModule,
    RvSortableModule,
    SharedModule,
    PurchaseModule,
    MatCheckboxModule,
    MatDialogModule,
    MatExpansionModule,
    MatInputModule,
    MatSlideToggleModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    ScrollingModule,
    MatProgressSpinnerModule,
    AIChatbotModule,
    AccessibilityModule,
    RvDraggableModule,
    NoResultsComponent,
    FolderPathComponent,
    NewFolderButtonComponent,
    OperationsMenuComponent,
    DialogHeaderComponent,
    StatusBadgeComponent,
  ],
  declarations: [
    PresentationListComponent,
    FooterComponent,
    ToolbarComponent,
    HtmlEditorComponent,
    ArtboardComponent,
    ArtboardPresentationComponent,
    ArtboardPlaceholderComponent,
    SidebarComponent,
    WorkspaceComponent,
    WorkspaceElementDirective,
    PlaceholderDragDirective,
    PlaceholderResizeDirective,
    PlaceholdersListComponent,
    PlaceholderPlaylistComponent,
    PlaceholderSettingsComponent,
    ResolutionSelectorComponent,
    PresentationPropertiesComponent,
    PlaylistItemComponent,
    PlaceholderNameDirective,
    WidgetItemComponent,
    WidgetSettingsComponent,
    PresentationItemComponent,
    AddPresentationComponent,
    ProductDetailsComponent,
    StoreContentComponent,
    WeeklyTemplatesComponent,
    EmbeddingPresentationsModalComponent,
    ViewportComponent,
    TemplateCalendarComponent,
    ReviewRequestComponent,
    RequestChangesDialogComponent,
  ],
  providers: [
    UsernamePipe,
    { provide: 'HasFolderStructure', useClass: EditorService },
    providePresentationFolderOperationService(),
  ],
})
export class EditorModule {}
